import React from 'react';
import PropTypes from 'prop-types';
import className from 'classnames';
import _taggedTemplateLiteralLoose from '@babel/runtime/helpers/esm/taggedTemplateLiteralLoose';
import styled from 'styled-components';
import { FormHelp, IconError, TextButton, Flex, Text, token, spaceMixin, VisuallyHidden, IconCheckmarkCircle, IconExclamationCircle } from '@zillow/constellation';
import _toConsumableArray from '@babel/runtime/helpers/esm/toConsumableArray';

var BodyWrapper = function BodyWrapper(_ref) {
  var children = _ref.children;
  return /*#__PURE__*/React.createElement("div", {
    className: "module-body"
  }, children);
};

BodyWrapper.propTypes = process.env.NODE_ENV !== "production" ? {
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.object])
} : {};

var Footer = function Footer(_ref) {
  var _className;

  var border = _ref.border,
      text = _ref.text,
      children = _ref.children;
  var classes = className('module-footer', (_className = {}, _className["line-" + border] = typeof border !== 'undefined', _className));
  return /*#__PURE__*/React.createElement("div", {
    className: classes
  }, text && /*#__PURE__*/React.createElement("span", null, text), children);
};

Footer.propTypes = process.env.NODE_ENV !== "production" ? {
  border: PropTypes.oneOf(['top', 'right', 'bottom', 'left', 'none']),
  children: PropTypes.node,
  text: PropTypes.string
} : {};

function _templateObject() {
  var data = _taggedTemplateLiteralLoose(["\n    font-size: 13px;\n    margin-top: 10px;\n\n    svg {\n        margin-right: 4px;\n    }\n"]);

  _templateObject = function _templateObject() {
    return data;
  };

  return data;
}
var StyledFormHelp = styled(FormHelp)(_templateObject());
StyledFormHelp.displayName = 'StyledFormHelp';

var FormHelpError = function FormHelpError(_ref) {
  var text = _ref.text;
  return /*#__PURE__*/React.createElement(StyledFormHelp, {
    error: true
  }, /*#__PURE__*/React.createElement(IconError, {
    fontColor: "red500"
  }), text);
};

FormHelpError.propTypes = process.env.NODE_ENV !== "production" ? {
  text: PropTypes.string.isRequired
} : {};

var Header = function Header(_ref) {
  var children = _ref.children,
      text = _ref.text;
  return /*#__PURE__*/React.createElement("div", {
    className: "module-head"
  }, /*#__PURE__*/React.createElement("div", {
    id: "sr-message",
    "aria-live": "off",
    className: "visually-hidden"
  }), /*#__PURE__*/React.createElement("h2", {
    className: "auth-heading"
  }, text), children && /*#__PURE__*/React.createElement("p", null, children));
};

Header.propTypes = process.env.NODE_ENV !== "production" ? {
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.object, PropTypes.string]),
  text: PropTypes.string.isRequired
} : {};

/**
 * This is for external links only. Not to be
 * confused with react-router's Link component.
 * @kind function
 */
var LINK_TARGETS = process.env.NODE_ENV !== "production" ? ['_blank', '_parent', '_self', '_top'] : {};

var Link = function Link(_ref) {
  var target = _ref.target,
      text = _ref.text,
      url = _ref.url;
  return /*#__PURE__*/React.createElement(TextButton, {
    as: "a",
    href: url,
    target: target,
    fontType: "finePrint"
  }, text);
};

Link.propTypes = process.env.NODE_ENV !== "production" ? {
  target: PropTypes.oneOf(LINK_TARGETS),
  text: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired
} : {};

var TermsOfUse = function TermsOfUse() {
  return /*#__PURE__*/React.createElement(Flex, {
    display: "flex",
    justifyContent: "center",
    marginBottom: "sm",
    marginTop: "sm"
  }, /*#__PURE__*/React.createElement(Text, {
    as: "p",
    fontType: "finePrint",
    fontColor: "textMedium",
    id: "terms-of-use-text"
  }, "By submitting, I accept Zillow's", ' ', /*#__PURE__*/React.createElement(Link, {
    text: "terms of use",
    url: "/corp/Terms.htm",
    target: "_blank"
  }), "."));
};

function _templateObject2() {
  var data = _taggedTemplateLiteralLoose(["\n    display: inline-block;\n    min-width: ", ";\n    padding-right: ", ";\n    > svg {\n        font-size: 18px;\n    }\n"]);

  _templateObject2 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$1() {
  var data = _taggedTemplateLiteralLoose(["\n    color: ", ";\n    font-size: ", "px;\n    padding: ", " 0;\n"]);

  _templateObject$1 = function _templateObject() {
    return data;
  };

  return data;
}

var getFontColor = function getFontColor(status) {
  switch (status) {
    case 'fail':
      return 'colors.red600';

    case 'pass':
      return 'colors.textDark';

    default:
      return 'colors.textMedium';
  }
};

var VALIDATION_ICONS = {
  neutral: null,
  pass: /*#__PURE__*/React.createElement(IconCheckmarkCircle, {
    fontColor: "green500"
  }),
  fail: /*#__PURE__*/React.createElement(IconExclamationCircle, {
    fontColor: "red500"
  })
};
var StyledMessage = styled.div(_templateObject$1(), function (p) {
  return token(getFontColor(p.status));
}, token('fontSizes.finePrint'), spaceMixin(0.25));
var StyledValidationIcon = styled.span(_templateObject2(), spaceMixin('md'), spaceMixin('xs'));

var ValidationMessage = function ValidationMessage(_ref) {
  var text = _ref.text,
      status = _ref.status,
      keyCount = _ref.keyCount;
  return /*#__PURE__*/React.createElement(StyledMessage, {
    status: status
  }, /*#__PURE__*/React.createElement(StyledValidationIcon, {
    "aria-hidden": "true"
  }, VALIDATION_ICONS[status]), /*#__PURE__*/React.createElement(VisuallyHidden, null, "validation " + status), /*#__PURE__*/React.createElement("span", {
    id: "validation-text-" + keyCount,
    tabIndex: "-1"
  }, text));
};

ValidationMessage.propTypes = process.env.NODE_ENV !== "production" ? {
  text: PropTypes.string.isRequired,
  status: PropTypes.oneOf(['pass', 'fail', 'neutral']),
  keyCount: PropTypes.number.isRequired
} : {};
ValidationMessage.defaultProps = {
  status: 'neutral'
};

var ViewWrapper = function ViewWrapper(_ref) {
  var children = _ref.children,
      className = _ref.className;
  return /*#__PURE__*/React.createElement("div", {
    className: "user-account " + className
  }, /*#__PURE__*/React.createElement("div", {
    className: "module-wrap"
  }, children));
};

ViewWrapper.propTypes = process.env.NODE_ENV !== "production" ? {
  // eslint-disable-next-line zillow/react/forbid-prop-types
  children: PropTypes.array,
  className: PropTypes.string
} : {};
ViewWrapper.defaultProps = {
  children: null,
  className: 'login'
};

/* eslint no-confusing-arrow: ["error", {"allowParens": true}] */

/**
 * Parses dictionary as URL encoded param string.
 * @kind function
 * @param  {Object} obj
 *                    data to be serialized
 * @return {String} encoded param string
 */
var serialize = function serialize(obj) {
  if (obj === void 0) {
    obj = {};
  }

  return Object.keys(obj).map(function (key) {
    return encodeURIComponent(key) + "=" + encodeURIComponent(obj[key]);
  }).join('&');
};
/**
 * Extracts unique values from object
 * @param {Object} obj
 * @return {Array} array of unique object values
 */

var uniqueObjectValuesToArray = function uniqueObjectValuesToArray(obj) {
  if (obj === void 0) {
    obj = {};
  }

  return Object.values(obj) // remove duplicates
  .reduce(function (accu, value) {
    return accu.indexOf(value) === -1 ? [].concat(_toConsumableArray(accu), [value]) : accu;
  }, []);
};

var forms = /*#__PURE__*/Object.freeze({
    __proto__: null,
    serialize: serialize,
    uniqueObjectValuesToArray: uniqueObjectValuesToArray
});

export { BodyWrapper, Footer, FormHelpError, Header, TermsOfUse, ValidationMessage, ViewWrapper, forms as formUtils };
